import { addDoc, collection, deleteDoc, doc, getDocs, query, updateDoc, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { parseContents } from '../utils';
import { ProjectErrorType } from '../../utils/errors/types';
import { getProjectErrorFromRawError } from '../../utils/errors';
import { Category, Goal, Pillar, Promotion, AuthorCategory } from '../types/contentManagement';
import { parseCategories, parseGoals, parsePillars, parsePromotions, parseAuthorCategories } from './parser';
import {
  CreateCategoryDto,
  CreateGoalDto,
  CreatePromotionDto,
  UpdateCategoryDto,
  UpdateGoalDto,
  UpdatePillarDto,
  UpdatePromotionDto,
  CreateAuthorCategoryDto,
  UpdateAuthorCategoryDto,
} from '../typeDtos/contentManagement';

export const getAllCategories = async (): Promise<Category[]> => {
  try {
    const _query = query(collection(db, 'categories'));
    const rawResult = await getDocs(_query);
    return await parseCategories(parseContents(rawResult));
  } catch (error) {
    console.log('[CATEGORIES] Error at getAllCategories:', error);
    return [];
  }
};

export const createCategory = async (data: CreateCategoryDto): Promise<ProjectErrorType | { success: boolean }> => {
  try {
    const nowTs = new Date().getTime();
    const _data = { ...data, createdAt: nowTs, updatedAt: nowTs };
    await addDoc(collection(db, 'categories'), _data);
    return { success: true };
  } catch (e) {
    console.log('[CATEGORIES] Error at createCategory:', e);
    return getProjectErrorFromRawError(e as Error);
  }
};

export const updateCategory = async (data: UpdateCategoryDto): Promise<ProjectErrorType | { success: boolean }> => {
  try {
    const nowTs = new Date().getTime();
    const _data = { ...data, updatedAt: nowTs };
    const docRef = doc(db, 'categories', _data?.id);
    await updateDoc(docRef, _data);
    return { success: true };
  } catch (e) {
    console.log('[CATEGORIES] Error at updateCategory:', e);
    return getProjectErrorFromRawError(e as Error);
  }
};

export const getAllGoals = async (filterProps?: {
  constraintField?: 'linkedCategories' | 'linkedPillars';
  constraintValue?: string;
}): Promise<Goal[]> => {
  try {
    let _query;
    if (!!filterProps?.constraintField && !!filterProps?.constraintValue) {
      const constraint = where(filterProps?.constraintField, 'array-contains', filterProps?.constraintValue);
      _query = query(collection(db, 'goals'), constraint);
    } else {
      _query = query(collection(db, 'goals'));
    }
    const rawResult = await getDocs(_query);
    return await parseGoals(parseContents(rawResult));
  } catch (error) {
    console.log('[GOALS] Error at getAllGoals' + (!!filterProps ? ` with props ${filterProps}:` : ':'), error);
    return [];
  }
};

export const createGoal = async (data: CreateGoalDto): Promise<ProjectErrorType | { success: boolean }> => {
  try {
    const nowTs = new Date().getTime();
    const _data = { ...data, createdAt: nowTs, updatedAt: nowTs };
    await addDoc(collection(db, 'goals'), _data);
    return { success: true };
  } catch (e) {
    console.log('[GOALS] Error at createGoal:', e);
    return getProjectErrorFromRawError(e as Error);
  }
};

export const updateGoal = async (data: UpdateGoalDto): Promise<ProjectErrorType | { success: boolean }> => {
  try {
    const nowTs = new Date().getTime();
    const _data = { ...data, updatedAt: nowTs };
    const docRef = doc(db, 'goals', _data?.id);
    await updateDoc(docRef, _data);
    return { success: true };
  } catch (e) {
    console.log('[GOALS] Error at updateGoal:', e);
    return getProjectErrorFromRawError(e as Error);
  }
};

export const getAllPillars = async (): Promise<Pillar[]> => {
  try {
    const _query = query(collection(db, 'pillars'));
    const rawResult = await getDocs(_query);
    return await parsePillars(parseContents(rawResult));
  } catch (error) {
    console.log('[PILLARS] Error at getAllPillars:', error);
    return [];
  }
};

export const updatePillar = async (data: UpdatePillarDto): Promise<ProjectErrorType | { success: boolean }> => {
  try {
    const nowTs = new Date().getTime();
    const _data = { ...data, updatedAt: nowTs };
    const docRef = doc(db, 'pillars', _data?.id);
    await updateDoc(docRef, _data);
    return { success: true };
  } catch (e) {
    console.log('[PILLARS] Error at updatePillar:', e);
    return getProjectErrorFromRawError(e as Error);
  }
};

export const getAllPromotions = async (): Promise<Promotion[]> => {
  try {
    const _query = query(collection(db, 'promotions'));
    const rawResult = await getDocs(_query);
    return await parsePromotions(parseContents(rawResult));
  } catch (error) {
    console.log('[PROMOTIONS] Error at getAllPromotions:', error);
    return [];
  }
};

export const createPromotion = async (data: CreatePromotionDto): Promise<ProjectErrorType | { success: boolean }> => {
  try {
    const nowTs = new Date().getTime();
    const _data = { ...data, createdAt: nowTs, updatedAt: nowTs };
    await addDoc(collection(db, 'promotions'), _data);
    return { success: true };
  } catch (e) {
    console.log('[PROMOTIONS] Error at createPromotion:', e);
    return getProjectErrorFromRawError(e as Error);
  }
};

export const updatePromotion = async (data: UpdatePromotionDto): Promise<ProjectErrorType | { success: boolean }> => {
  try {
    const nowTs = new Date().getTime();
    const _data = { ...data, updatedAt: nowTs };
    const docRef = doc(db, 'promotions', _data?.id);
    await updateDoc(docRef, _data);
    return { success: true };
  } catch (e) {
    console.log('[PROMOTIONS] Error at updatePromotion:', e);
    return getProjectErrorFromRawError(e as Error);
  }
};

export const deletePromotion = async (id: string): Promise<ProjectErrorType | { success: boolean }> => {
  try {
    const docRef = doc(db, 'promotions', id);
    await deleteDoc(docRef);
    return { success: true };
  } catch (e) {
    console.log('[PROMOTIONS] Error at deletePromotion:', e);
    return getProjectErrorFromRawError(e as Error);
  }
};

export const getAllAuthorCategories = async (): Promise<AuthorCategory[]> => {
  try {
    const _query = query(collection(db, 'authorCategories'));
    const rawResult = await getDocs(_query);
    return await parseAuthorCategories(parseContents(rawResult));
  } catch (error) {
    console.log('[AUTHOR_CATEGORIES] Error at getAllAuthorCategories:', error);
    return [];
  }
};

export const createAuthorCategory = async (
  data: CreateAuthorCategoryDto,
): Promise<ProjectErrorType | { success: boolean }> => {
  try {
    const nowTs = new Date().getTime();
    const _data = { ...data, createdAt: nowTs, updatedAt: nowTs };
    await addDoc(collection(db, 'authorCategories'), _data);
    return { success: true };
  } catch (e) {
    console.log('[AUTHOR_CATEGORIES] Error at createAuthorCategory:', e);
    return getProjectErrorFromRawError(e as Error);
  }
};

export const updateAuthorCategory = async (
  data: UpdateAuthorCategoryDto,
): Promise<ProjectErrorType | { success: boolean }> => {
  try {
    const nowTs = new Date().getTime();
    const _data = { ...data, updatedAt: nowTs };
    const docRef = doc(db, 'authorCategories', _data?.id);
    await updateDoc(docRef, _data);
    return { success: true };
  } catch (e) {
    console.log('[AUTHOR_CATEGORIES] Error at updateAuthorCategory:', e);
    return getProjectErrorFromRawError(e as Error);
  }
};
